/**
 * @voltxampere/ui — Bode plot Canvas2D renderer (Sprint 20)
 *
 * Pure rendering helper consumed by `<vxa-bode-plot>`. Splits the
 * canvas into two stacked panes — magnitude (upper half) and phase
 * (lower half) — both sharing a logarithmic frequency X-axis.
 *
 * Mode-independent rules:
 *  - X axis: log10(freq) from min(frequencies) to max(frequencies).
 *    Major gridlines at every decade, minor gridlines at 2/5/8 of
 *    the decade for visual reference.
 *  - Y axis (magnitude): linear 0..max(|H|)*1.05 OR dB 20·log10(|H|).
 *    The component picks linear by default; users can toggle to dB
 *    via the renderer's `magnitudeUnit` option.
 *  - Y axis (phase): degrees -180..+180 OR radians -π..+π. Default
 *    is degrees (intuitive for non-EE readers).
 *  - Channel colors: ZENIT brand `--brass-canon-*` palette per
 *    BLUEPRINT §7.2. Sprint 17/18 audit D0 noted CH-3 has two
 *    candidate values (#D19648 in canonical ZENIT_BRAND.md vs
 *    #C08A3E in the canonical platform mockup); Sprint 20 D-20-8
 *    decision: this Bode plot uses the platform-mockup canon
 *    `#C08A3E` so Şefim's printed mockup matches the runtime UI.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
const CANVAS_PADDING_X = 48;
const CANVAS_PADDING_TOP = 24;
const CANVAS_PADDING_BOTTOM = 28;
const SEPARATOR_PAD = 12;
const GRID_COLOR = "rgba(180, 180, 200, 0.12)";
const GRID_MAJOR_COLOR = "rgba(180, 180, 200, 0.22)";
const TEXT_COLOR = "rgba(220, 220, 230, 0.7)";
const AXIS_LABEL_FONT = "10px JetBrains Mono, ui-monospace, monospace";
const TRACE_LINE_WIDTH = 1.6;
const PHASE_DASH = [4, 3];
/**
 * Sprint 20 D-20-8 — CH-3 token reconciliation. Canonical platform
 * mockup uses `#C08A3E`; ZENIT_BRAND.md prose uses `#D19648`. Bode
 * plot follows the mockup so the printed Şefim mockup matches the
 * runtime UI. Mevcut `<vxa-waveform>` keeps `#D19648` until Sprint
 * 21 sweeps the global token rename.
 */
export const BODE_CHANNEL_COLORS = ["#7BB896", "#6EA3C8", "#C08A3E", "#A78BC8"];
export function bodeChannelColor(index) {
    const c = BODE_CHANNEL_COLORS[index % BODE_CHANNEL_COLORS.length];
    return c ?? "#C08A3E";
}
function magToY(value, unit, paneMax, paneMin) {
    const cv = unit === "dB" ? toDb(value) : value;
    const span = paneMax - paneMin;
    if (span <= 0)
        return 0.5;
    return (cv - paneMin) / span;
}
function toDb(linear) {
    if (linear <= 0)
        return -200; // sentinel floor
    return 20 * Math.log10(linear);
}
function phaseToY(rad, unit) {
    // Normalise to [-π, π] then map to [0, 1] (top = +π).
    const wrapped = wrapToPi(rad);
    const value = unit === "deg" ? (wrapped * 180) / Math.PI : wrapped;
    const span = unit === "deg" ? 360 : 2 * Math.PI;
    const min = unit === "deg" ? -180 : -Math.PI;
    return (value - min) / span;
}
function wrapToPi(rad) {
    let r = rad;
    while (r > Math.PI)
        r -= 2 * Math.PI;
    while (r < -Math.PI)
        r += 2 * Math.PI;
    return r;
}
function freqToX(freq, fMin, fMax) {
    if (fMax <= fMin || freq <= 0)
        return 0;
    const logSpan = Math.log10(fMax) - Math.log10(fMin);
    return (Math.log10(freq) - Math.log10(fMin)) / logSpan;
}
function drawXAxisGrid(ctx, pane, fMin, fMax, drawLabels) {
    if (fMax <= fMin)
        return;
    const logMin = Math.floor(Math.log10(fMin));
    const logMax = Math.ceil(Math.log10(fMax));
    ctx.save();
    ctx.font = AXIS_LABEL_FONT;
    ctx.fillStyle = TEXT_COLOR;
    ctx.textBaseline = "top";
    for (let lg = logMin; lg <= logMax; lg++) {
        const f = Math.pow(10, lg);
        const x = pane.x + freqToX(f, fMin, fMax) * pane.w;
        ctx.strokeStyle = GRID_MAJOR_COLOR;
        ctx.beginPath();
        ctx.moveTo(x, pane.y);
        ctx.lineTo(x, pane.y + pane.h);
        ctx.stroke();
        if (drawLabels) {
            const label = formatFreqLabel(f);
            ctx.textAlign = "center";
            ctx.fillText(label, x, pane.y + pane.h + 4);
        }
        // minor lines at 2 / 5 / 8 within decade
        for (const m of [2, 5, 8]) {
            const fm = m * f;
            if (fm >= fMin && fm <= fMax) {
                const xm = pane.x + freqToX(fm, fMin, fMax) * pane.w;
                ctx.strokeStyle = GRID_COLOR;
                ctx.beginPath();
                ctx.moveTo(xm, pane.y);
                ctx.lineTo(xm, pane.y + pane.h);
                ctx.stroke();
            }
        }
    }
    ctx.restore();
}
function formatFreqLabel(f) {
    if (f >= 1e9)
        return `${String(f / 1e9)}G`;
    if (f >= 1e6)
        return `${String(f / 1e6)}M`;
    if (f >= 1e3)
        return `${String(f / 1e3)}k`;
    if (f >= 1)
        return String(Math.round(f));
    if (f >= 1e-3)
        return `${String(Math.round(f * 1e3))}m`;
    return f.toExponential(0);
}
function drawMagnitudePane(ctx, pane, frame) {
    // Auto-range Y axis.
    let maxVal = 0;
    let minVal = Number.POSITIVE_INFINITY;
    for (const tr of frame.traces) {
        for (const m of tr.magnitudes) {
            const cv = frame.magnitudeUnit === "dB" ? toDb(m) : m;
            if (cv > maxVal)
                maxVal = cv;
            if (cv < minVal)
                minVal = cv;
        }
    }
    if (!Number.isFinite(minVal))
        minVal = 0;
    if (frame.magnitudeUnit === "linear") {
        minVal = 0;
        maxVal = Math.max(maxVal * 1.05, 1e-6);
    }
    else {
        // Snap to nearest 10 dB.
        minVal = Math.floor(minVal / 10) * 10 - 10;
        maxVal = Math.ceil(maxVal / 10) * 10 + 5;
    }
    ctx.save();
    ctx.strokeStyle = GRID_MAJOR_COLOR;
    ctx.strokeRect(pane.x, pane.y, pane.w, pane.h);
    // Y gridlines: 4 horizontal lines.
    ctx.font = AXIS_LABEL_FONT;
    ctx.fillStyle = TEXT_COLOR;
    ctx.textBaseline = "middle";
    ctx.textAlign = "right";
    for (let i = 0; i <= 4; i++) {
        const yFrac = i / 4;
        const yPx = pane.y + yFrac * pane.h;
        ctx.strokeStyle = GRID_COLOR;
        ctx.beginPath();
        ctx.moveTo(pane.x, yPx);
        ctx.lineTo(pane.x + pane.w, yPx);
        ctx.stroke();
        const yVal = maxVal - yFrac * (maxVal - minVal);
        const label = frame.magnitudeUnit === "dB" ? `${yVal.toFixed(0)}dB` : yVal.toFixed(2);
        ctx.fillText(label, pane.x - 4, yPx);
    }
    // Trace lines.
    for (const tr of frame.traces) {
        if (tr.magnitudes.length === 0)
            continue;
        ctx.beginPath();
        ctx.strokeStyle = tr.color;
        ctx.lineWidth = TRACE_LINE_WIDTH;
        let started = false;
        for (let i = 0; i < tr.magnitudes.length; i++) {
            const f = frame.frequencies[i] ?? 0;
            const m = tr.magnitudes[i] ?? 0;
            const x = pane.x +
                freqToX(f, frame.frequencies[0] ?? 1, frame.frequencies[frame.frequencies.length - 1] ?? 1) *
                    pane.w;
            const yFrac = magToY(m, frame.magnitudeUnit, maxVal, minVal);
            const y = pane.y + (1 - yFrac) * pane.h;
            if (!started) {
                ctx.moveTo(x, y);
                started = true;
            }
            else {
                ctx.lineTo(x, y);
            }
        }
        ctx.stroke();
    }
    // Top-left axis title.
    ctx.fillStyle = TEXT_COLOR;
    ctx.textAlign = "left";
    ctx.textBaseline = "top";
    ctx.fillText(frame.magnitudeUnit === "dB" ? "|H|·dB" : "|H|", pane.x + 4, pane.y + 2);
    ctx.restore();
}
function drawPhasePane(ctx, pane, frame) {
    ctx.save();
    ctx.strokeStyle = GRID_MAJOR_COLOR;
    ctx.strokeRect(pane.x, pane.y, pane.w, pane.h);
    // Y gridlines: 5 horizontal lines representing -180/-90/0/+90/+180.
    ctx.font = AXIS_LABEL_FONT;
    ctx.fillStyle = TEXT_COLOR;
    ctx.textBaseline = "middle";
    ctx.textAlign = "right";
    for (let i = 0; i <= 4; i++) {
        const yFrac = i / 4;
        const yPx = pane.y + yFrac * pane.h;
        ctx.strokeStyle = i === 2 ? GRID_MAJOR_COLOR : GRID_COLOR;
        ctx.beginPath();
        ctx.moveTo(pane.x, yPx);
        ctx.lineTo(pane.x + pane.w, yPx);
        ctx.stroke();
        const value = frame.phaseUnit === "deg" ? 180 - 90 * i : Math.PI - (Math.PI / 2) * i;
        const label = frame.phaseUnit === "deg" ? `${value.toFixed(0)}°` : value.toFixed(2);
        ctx.fillText(label, pane.x - 4, yPx);
    }
    // Phase trace (dashed).
    for (const tr of frame.traces) {
        if (tr.phases.length === 0)
            continue;
        ctx.beginPath();
        ctx.strokeStyle = tr.color;
        ctx.lineWidth = TRACE_LINE_WIDTH;
        ctx.setLineDash(PHASE_DASH);
        let started = false;
        for (let i = 0; i < tr.phases.length; i++) {
            const f = frame.frequencies[i] ?? 0;
            const p = tr.phases[i] ?? 0;
            const x = pane.x +
                freqToX(f, frame.frequencies[0] ?? 1, frame.frequencies[frame.frequencies.length - 1] ?? 1) *
                    pane.w;
            const yFrac = phaseToY(p, frame.phaseUnit);
            const y = pane.y + (1 - yFrac) * pane.h;
            if (!started) {
                ctx.moveTo(x, y);
                started = true;
            }
            else {
                ctx.lineTo(x, y);
            }
        }
        ctx.stroke();
        ctx.setLineDash([]);
    }
    ctx.fillStyle = TEXT_COLOR;
    ctx.textAlign = "left";
    ctx.textBaseline = "top";
    ctx.fillText(`∠H · ${frame.phaseUnit}`, pane.x + 4, pane.y + 2);
    ctx.restore();
}
/**
 * Draw a complete Bode plot frame onto the supplied 2D context.
 * Caller is responsible for clearing the canvas beforehand and for
 * sizing the canvas to match the device pixel ratio.
 */
export function renderBodeFrame(ctx, frame) {
    const { width, height } = frame;
    const usableW = width - CANVAS_PADDING_X * 2;
    const usableH = height - CANVAS_PADDING_TOP - CANVAS_PADDING_BOTTOM;
    const paneH = (usableH - SEPARATOR_PAD) / 2;
    const fMin = frame.frequencies[0] ?? 1;
    const fMax = frame.frequencies[frame.frequencies.length - 1] ?? 1;
    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = "rgba(8, 12, 23, 0.92)"; // ZENIT ink
    ctx.fillRect(0, 0, width, height);
    const magPane = {
        x: CANVAS_PADDING_X,
        y: CANVAS_PADDING_TOP,
        w: usableW,
        h: paneH,
    };
    const phasePane = {
        x: CANVAS_PADDING_X,
        y: CANVAS_PADDING_TOP + paneH + SEPARATOR_PAD,
        w: usableW,
        h: paneH,
    };
    drawXAxisGrid(ctx, magPane, fMin, fMax, false);
    drawMagnitudePane(ctx, magPane, frame);
    drawXAxisGrid(ctx, phasePane, fMin, fMax, true);
    drawPhasePane(ctx, phasePane, frame);
}
//# sourceMappingURL=bode-renderer.js.map